/**
 * FinancialDashboard
 * Dashboard page for financial module with role-adaptive content
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import {
  AttachMoney,
  Receipt,
  TrendingUp,
  HourglassEmpty,
  MedicalServices,
  Devices,
  Refresh,
  ListAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { invoiceService, DashboardData } from '../../services/invoiceService';
import { formatCurrency } from '../../utils/financialHelpers';

// ============================================================================
// STAT CARD COMPONENT (local, simple)
// ============================================================================

interface FinancialStatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
  onClick?: () => void;
}

const FinancialStatCard: React.FC<FinancialStatCardProps> = ({
  icon,
  label,
  value,
  color,
  subtitle,
  onClick,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick
          ? { transform: 'translateY(-2px)', boxShadow: 4 }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: 700, color: `${color}.main`, mb: 0.5 }}
            >
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette[color].main, 0.1),
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// HELPERS
// ============================================================================

function calcChangePercent(current: number, previous: number): string {
  if (previous === 0) return current > 0 ? '+100%' : '0%';
  const pct = ((current - previous) / previous) * 100;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FinancialDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboardData = await invoiceService.getDashboard();
      setData(dashboardData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar dados do dashboard financeiro';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentMonth = data?.currentMonth;
  const previousMonth = data?.previousMonth;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Financeiro' },
      ]}
      title="Financeiro"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<ListAlt />}
            onClick={() => navigate('/financial/invoices')}
          >
            Ver Faturas
          </Button>
          <Button
            variant="text"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            Atualizar
          </Button>
        </>
      }
    >
        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Dashboard Cards */}
        {!loading && data && currentMonth && (
          <>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<Receipt />}
                label="Total de Faturas"
                value={currentMonth.totalInvoices.toLocaleString('pt-BR')}
                color="primary"
                subtitle={
                  previousMonth
                    ? `${calcChangePercent(currentMonth.totalInvoices, previousMonth.totalInvoices)} vs. mes anterior`
                    : undefined
                }
                onClick={() => navigate('/financial/invoices')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<AttachMoney />}
                label="Valor Total"
                value={formatCurrency(currentMonth.totalAmount)}
                color="success"
                subtitle={
                  previousMonth
                    ? `${calcChangePercent(currentMonth.totalAmount, previousMonth.totalAmount)} vs. mes anterior`
                    : undefined
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<MedicalServices />}
                label="Faturas de Serviço"
                value={formatCurrency(currentMonth.serviceAmount)}
                color="info"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<Devices />}
                label="Faturas de Plataforma"
                value={formatCurrency(currentMonth.platformAmount)}
                color="secondary"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<HourglassEmpty />}
                label="Faturas Pendentes"
                value={currentMonth.pendingCount.toLocaleString('pt-BR')}
                color="warning"
                onClick={() => navigate('/financial/invoices?status=OPEN')}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FinancialStatCard
                icon={<AttachMoney />}
                label="A Receber"
                value={formatCurrency(data.receivable)}
                color="info"
                subtitle="Faturas fechadas não pagas"
              />
            </Grid>

            {data.overdue > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FinancialStatCard
                  icon={<HourglassEmpty />}
                  label="Vencidas"
                  value={formatCurrency(data.overdue)}
                  color="error"
                  onClick={() => navigate('/financial/invoices?status=OVERDUE')}
                />
              </Grid>
            )}

            {previousMonth && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FinancialStatCard
                  icon={<TrendingUp />}
                  label="Mes Anterior"
                  value={formatCurrency(previousMonth.totalAmount)}
                  color="info"
                  subtitle={`${previousMonth.totalInvoices} faturas`}
                />
              </Grid>
            )}
          </Grid>

          {/* Status Breakdown */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resumo por Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {([
                { key: 'OPEN' as const, label: 'Abertas', color: 'warning' as const },
                { key: 'CLOSED' as const, label: 'Fechadas', color: 'info' as const },
                { key: 'PAID' as const, label: 'Pagas', color: 'success' as const },
                { key: 'CANCELLED' as const, label: 'Canceladas', color: 'default' as const },
              ]).map(({ key, label, color }) => {
                const item = data.statusBreakdown[key];
                return (
                  <Chip
                    key={key}
                    label={`${label}: ${item.count} (${formatCurrency(item.totalAmount)})`}
                    color={color}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </Box>

          {/* Monthly Evolution Chart */}
          {data.monthlyEvolution && data.monthlyEvolution.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Evolução Mensal
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.monthlyEvolution.map((item) => ({
                    name: `${String(item.month).padStart(2, '0')}/${String(item.year).slice(2)}`,
                    valor: item.totalAmount,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v: number | string) => formatCurrency(Number(v))} />
                    <RechartsTooltip formatter={(v: ValueType | undefined) => formatCurrency(Number(v ?? 0))} />
                    <Bar dataKey="valor" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Empty state */}
        {!loading && !error && !currentMonth && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Nenhum dado financeiro disponível para o período atual.
          </Alert>
        )}
    </PageShell>
  );
};

export default FinancialDashboard;
